import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Box, Fade, Menu, MenuItem } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ACTION_MENU, ALPHA, CONTROL_HEIGHT, FONT_SIZE, INK, MOTION, NEUTRAL, SHADOW } from "@/theme/tokens";
import { portalOverlayProps } from "@/ui/Popups/portalOverlay";
import { SystemTabLabel } from "./SystemTabLabel";
import type { SystemTab } from "./utils";

type Props<T> = {
	/** The "More" tab while its menu is open; null closes it. */
	anchorEl: HTMLElement | null;
	/** The tabs that moved out of the bar, in bar order. */
	tabs: readonly SystemTab<T>[];
	currentTab: T | undefined;
	/** Already translated: the menu's accessible name. */
	label: string;
	onPick: (value: T) => void;
	onClose: () => void;
	/** The bar's test id; each item keeps its tab's own id (`{dataTestId}-{value}`), the menu is `{dataTestId}-more-menu`. */
	dataTestId?: string;
};

/**
 * The tab bar's "More" menu: the tabs that didn't fit, each as it reads in the bar (label and badge), the one on screen
 * checked. Opens under the "More" tab's end; picking one switches to it and closes the menu.
 */
export const MoreTabMenu = <T,>({ anchorEl, tabs, currentTab, label, onPick, onClose, dataTestId }: Props<T>) => (
	<Menu
		anchorEl={anchorEl}
		open={anchorEl !== null}
		onClose={onClose}
		anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
		transformOrigin={{ vertical: "top", horizontal: "right" }}
		slots={{ transition: Fade }}
		transitionDuration={{ enter: MOTION.DURATION.ENTRANCE_SM, exit: MOTION.DURATION.INLINE_EXIT }}
		slotProps={{
			root: { ...portalOverlayProps("tabs-more-menu") },
			paper: {
				// A data attribute rides the slot props through MUI's spread; the slot type has no index signature for it.
				...({ "data-testid": dataTestId ? `${dataTestId}-more-menu` : undefined } as Record<string, string | undefined>),
				sx: { mt: 0.5, minWidth: ACTION_MENU.MIN_WIDTH_PX, borderRadius: 1.5, border: `1px solid ${NEUTRAL[200]}`, boxShadow: SHADOW.POPOVER_FLOAT },
			},
			list: { dense: true, sx: { py: 0.5 }, "aria-label": label },
		}}
	>
		{tabs.map((tab) => {
			const selected = tab.value === currentTab;
			return (
				<MenuItem
					key={String(tab.value)}
					role="menuitemradio"
					aria-checked={selected}
					disabled={tab.disabled}
					onClick={() => onPick(tab.value)}
					data-testid={dataTestId ? `${dataTestId}-${String(tab.value)}` : undefined}
					sx={(theme) => ({
						gap: 1.25,
						mx: 0.5,
						px: 1.25,
						minHeight: CONTROL_HEIGHT.COMPACT,
						borderRadius: 1,
						fontSize: FONT_SIZE.EMPHASIS,
						fontWeight: selected ? 600 : 400,
						color: selected ? theme.palette.primary.dark : INK.PRIMARY,
						"& .MuiSvgIcon-root": { fontSize: FONT_SIZE.TITLE_SM, color: INK.MUTED, flexShrink: 0 },
						"&:hover, &.Mui-focusVisible": { backgroundColor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) },
					})}
				>
					{tab.icon}
					<Box component="span" sx={{ flex: 1, minWidth: 0 }}>
						<SystemTabLabel label={tab.label} badge={tab.badge} gap={0.75} />
					</Box>
					{selected && <CheckRoundedIcon />}
				</MenuItem>
			);
		})}
	</Menu>
);
